import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import * as authService from './authService';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const getFirebaseApp = () => (getApps().length ? getApp() : initializeApp(firebaseConfig));

/* ---------------------------------- REFETCH EVENTING ---------------------------------- */
// Browser-native equivalent of the mobile app's eventBus: components subscribe to a named
// event (e.g. "refetchInvitations") and this fires it when a push is received in the foreground.
export const addRefetchListener = (eventName: string, handler: () => void) => {
  const listener = () => handler();
  window.addEventListener(eventName, listener);
  return () => window.removeEventListener(eventName, listener);
};

export const emitRefetch = (eventName: string) => {
  window.dispatchEvent(new CustomEvent(eventName));
};

/* ---------------------------------- REGISTER ---------------------------------- */
export const registerToken = async () => {
  try {
    if (!('serviceWorker' in navigator) || !(await isSupported())) {
      console.log('FCM is not supported in this browser');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission not granted');
      return;
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const messaging = getMessaging(getFirebaseApp());

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      await authService.registerForPushNotifications(token);
    }

    // Foreground messages: FCM doesn't auto-display these, and the tab is already open,
    // so just let any mounted screen (e.g. the header bell badge) refetch via the event.
    // Every notification bumps the bell badge regardless of its specific event, plus
    // whatever event it actually carries (e.g. refetchDiscussionNotes) for screen-specific refresh.
    onMessage(messaging, (payload) => {
      const data: any = payload?.data || {};

      emitRefetch('refetchNotificationsCount');
      if (data.event) {
        emitRefetch(data.event);
      }
    });

    // Background messages (tab open but unfocused, or a click on the resulting OS
    // notification) are handled by the service worker, which relays them here.
    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, data } = event.data || {};
      if (type === 'push-received') {
        emitRefetch('refetchNotificationsCount');
      }
      if ((type === 'push-received' || type === 'notification-click') && data?.event) {
        emitRefetch(data.event);
      }
    });
  } catch (error) {
    console.error('Failed to register push notifications:', error);
  }
};
